/**
 * Route copy for `src/routes/outreach` (keys: `routes.outreach.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  access: {
    actionReservedForEditorsYouAre: 'Action reserved for editors — you are read-only.',
  },
  campaignsPage: {
    name: 'Name',
    template: 'Template',
    senderIdentity: 'Sender identity',
    newCampaign: '+ New campaign',
    campaigns: 'Campaigns',
    analysisRecommendationsProspecting: 'Analysis & recommendations — prospecting',
    noCampaigns: 'No campaigns',
    createACampaignTemplateSenderIdentity: 'Create a campaign: template + sender identity + audience.',
    createACampaign: 'Create a campaign',
    recipientsRecipientS: '{{recipients}} recipient(s)',
    campaignDetails: 'Campaign details',
    failedError: 'Failed: {{error}}',
    sentStatusStatus: 'Sent (status: {{status}}).',
    templateName: 'Template: {{name}}',
    noTemplate: 'No template',
    fromFromEmail: 'From: {{from_email}}',
    noSender: 'No sender',
    scheduledOnV: ' · Scheduled on {{v}}',
    checkTheLegalBasisCaslLaw: 'Check the legal basis (CASL / Law 25) of each recipient before sending',
    auditCompliance: 'Audit compliance',
    sendATest: 'Send a test',
    schedule: 'Schedule',
    edit: 'Edit',
    deleteTheCampaign: 'Delete the campaign',
    loadingStatistics: 'Loading statistics…',
    noDataYet: 'No data yet',
    statisticsWillAppearAfterTheCampaign: 'Statistics will appear after the campaign is sent.',
    testAddress: 'Test address',
    youExampleCom: 'you@example.com',
    sending: 'Sending…',
    test: 'Test',
    close: 'Close',
    scheduleTheSend: 'Schedule the send',
    dateAndTime: 'Date and time',
    sendNow: 'Send now',
    sendNowTriggersAnImmediateIrreversible: '“Send now” triggers an immediate, irreversible real send.',
    sendImmediately: 'Send immediately?',
    theCampaignNameWillBeSent: 'The campaign “{{name}}” will be sent right away to the whole audience. This cannot be undone.',
    deleteTheCampaignNameThisCannot: 'Delete the campaign “{{name}}”? This cannot be undone.',
    allowed: 'Allowed',
    blocked: 'Blocked',
    undetermined: 'Undetermined',
    error: 'Error',
    auditLimitedToTheFirstPreflight: 'Audit limited to the first {{PREFLIGHT_MAX}} recipients out of {{length}}.',
    requestLimitReachedTryAgainIn: 'Request limit reached. Try again in a moment.',
    complianceAuditUnavailable: 'Compliance audit unavailable.',
    preSendComplianceAudit: 'Pre-send compliance audit',
    checksTheLegalBasisCaslLaw: 'Checks the legal basis (CASL · Law 25) of each recipient of “{{name}}”. The send gate stays fail-closed: a recipient blocked here will not receive the campaign.',
    emptyAudience: 'Empty audience',
    addRecipientsToTheCampaignBefore: 'Add recipients to the campaign before auditing compliance.',
    auditingDoneLength: 'Auditing… {{done}}/{{length}}',
    rerunTheAudit: 'Rerun the audit',
    auditLengthRecipientS: 'Audit {{length}} recipient(s)',
    allowed2: 'Allowed',
    blocked2: 'Blocked',
    other: 'Other',
    evaluatingRecipients: 'Evaluating recipients…',
    recipient: 'Recipient',
    verdict: 'Verdict',
    basisReason: 'Basis / reason',
    messages: 'Messages',
    delivered: 'Delivered',
    bounces: 'Bounces',
    unsubscribed: 'Unsubscribed',
    deliverability: 'Deliverability',
    opens: 'Opens',
    clicks: 'Clicks',
    responses: 'Responses',
    engagementFunnel: 'Engagement funnel',
    sent: 'Sent',
    trend30Days: 'Trend — 30 days',
    timeSeriesUnavailableOnThisBackend: 'Time series unavailable on this backend.',
    noSendsInTheLast30: 'No sends in the last 30 days.',
    sends: 'Sends',
    dailySends: 'Daily sends',
    dailyOpens: 'Daily opens',
    recommendations: 'Recommendations',
    healthyNothingToReport: 'Healthy — nothing to report.',
    bestSendTime: 'Best send time',
    perRecipientDetail: 'Per-recipient detail',
    sendDetailsUnavailableOnThisBackend: 'Send details unavailable on this backend.',
    noMessagesForThisCampaign: 'No messages for this campaign.',
    status: 'Status',
    sent2: 'Sent',
    open: 'Open',
    clicked: 'Clicked',
    replied: 'Replied',
    breakdownByLink: 'Breakdown by link',
    linkBreakdownUnavailableOnThisBackend: 'Link breakdown unavailable on this backend.',
    noLinkClickedForThisCampaign: 'No link clicked for this campaign.',
    link: 'Link',
    sendsWithThisLink: 'Sends with this link',
    timelineUnavailableForThisMessage: 'Timeline unavailable for this message.',
    loading: 'Loading…',
    noTrackingEventForThisMessage: 'No tracking event for this message.',
    campaign: 'Campaign',
    repl: 'Repl.',
    bounce: 'Bounce',
    campaignPerformance: 'Campaign performance',
    performanceReportUnavailableOnThisBacken: 'Performance report unavailable on this backend.',
    noPerformanceDataYet: 'No performance data yet.',
    addingTheAttachmentFailedTypeOr: 'Adding the attachment failed (type or size refused).',
    editTheCampaign: 'Edit the campaign',
    newCampaign2: 'New campaign',
    chooseATemplate: '— Choose a template —',
    chooseASender: '— Choose a sender —',
    audience: 'Audience',
    oneEmailAddressPerLineOr: 'One email address per line (or comma-separated).',
    contact1ExampleComContact2ExampleCom: 'contact1@example.com\ncontact2@example.com',
    fromAGroupDistributionList: '+ From a group / distribution list',
    attachments: 'Attachments',
    deckPdfDocumentSentWithEvery: 'Deck, PDF, document — sent with every message of this campaign.',
    vKb: '({{v}} KB)',
    removeFilename: 'Remove {{filename}}',
    attachAFile: '+ Attach a file',
    tracking: 'Tracking',
    turnOffForADeliverabilitySensitive: 'Turn off for a deliverability-sensitive send. The unsubscribe link and the List-Unsubscribe header are always present (CASL/CAN-SPAM).',
    trackOpensOpenPixel: 'Track opens (open pixel)',
    trackClicksLinkRewriting: 'Track clicks (link rewriting)',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    opens2: 'Opens',
  },
  components: {
    verified: '✓ Verified',
    notVerified: '⚠ Not verified',
    close: 'Close',
    loading: 'Loading…',
    copied: '✓ Copied',
    copy: 'Copy',
    delete: 'Delete',
    cancel: 'Cancel',
  },
  dialerPage: {
    calls: 'Calls',
    dialAnOutboundCallAndLog: 'Dial an outbound call and log its outcome. Recordings, subject to consent, are handled on the server and appear here when available.',
    readOnly: 'Read-only',
  },
  distributionGroupPicker: {
    errorStatus: 'Error {{status}}',
    lengthRecipientSFromTheGroup: '{{length}} recipient(s) from the group “{{v}}”',
    lengthRecipientSFromTheList: '{{length}} recipient(s) from the list “{{name}}”',
    lengthRecipientSFromTheSegment: '{{length}} recipient(s) from the segment “{{v}}”',
    lengthRecipientSFromTheSaved: '{{length}} recipient(s) from the saved search “{{v}}”',
    selectASource: 'Select a source.',
    skippedSuppressedSuppressed: ', {{skipped_suppressed}} suppressed',
    resolvedRecipientSResolvedSuppressed: '{{resolved}} recipient(s) resolved{{suppressed}}',
    thisSourceContainsNoRecipientWith: 'This source contains no recipient with an email.',
    resolvingTheSourceFailed: 'Resolving the source failed.',
    group: 'Group',
    distributionList: 'Distribution list',
    b2bSegment: 'B2B segment',
    b2bSavedSearch: 'B2B saved search',
    noCrmGroupsCreateAGroup: 'No CRM groups. Create a group in the CRM to use it here.',
    noSavedDistributionList: 'No saved distribution list.',
    noB2bContactSegmentCreateA: 'No B2B contact segment. Create a segment (“contacts” entity) in the B2B module to use it here.',
    noSavedB2bSearchContactsEntity: 'No saved B2B search (“contacts” entity). Save a search in the B2B module to use it here.',
    addFromADistributionSource: 'Add from a distribution source',
    sourceType: 'Source type',
    crmGroup: 'CRM group',
    choose: '— Choose —',
    manageDistributionLists: 'Manage distribution lists',
    recipientsAreAddedToTheAudience: 'Recipients are added to the audience (deduplicated). Each send is still filtered by the anti-spam / consent check (CASL/Law 25) at sending time.',
    cancel: 'Cancel',
    resolving: 'Resolving…',
    addTheRecipients: 'Add the recipients',
  },
  distributionListsManager: {
    resync: 'Re-sync',
    manual: 'Manual',
    crmGroup: 'CRM group',
    b2bSegment: 'B2B segment',
    b2bSearch: 'B2B search',
    errorStatus: 'Error {{status}}',
    theOperationFailedTryAgain: 'The operation failed. Try again.',
    listRenamed: 'List renamed.',
    listReSyncedContactCountContact: 'List re-synced ({{contact_count}} contact(s)).',
    listDeleted: 'List deleted.',
    manageDistributionLists: 'Manage distribution lists',
    savedLists: 'Saved lists',
    noDistributionLists: 'No distribution lists',
    createAReusableListFromA: 'Create a reusable list from a group, a segment or typed addresses.',
    close: 'Close',
    deleteTheList: 'Delete the list',
    deleteTheListNameThisCannot: 'Delete the list “{{name}}”? This cannot be undone.',
    listNameName: 'List name {{name}}',
    contactCountContactS: '{{contact_count}} contact(s)',
    syncV: '· sync {{v}}',
    deleteName: 'Delete {{name}}',
    aNameIsRequired: 'A name is required.',
    enterAtLeastOneEmailAddress: 'Enter at least one email address.',
    selectASource: 'Select a source.',
    listNameCreatedResolvedContactS: 'List “{{name}}” created ({{resolved}} contact(s)).',
    newList: '+ New list',
    newDistributionList: 'New distribution list',
    name: 'Name',
    eGActiveCustomers: 'E.g. Active customers',
    buildFrom: 'Build from',
    typedAddresses: 'Typed addresses',
    b2bSavedSearch: 'B2B saved search',
    addresses: 'Addresses',
    oneAddressPerLineOrComma: 'One address per line (or comma-separated).',
    contact1ExampleComContact2ExampleCom: 'contact1@example.com\ncontact2@example.com',
    source: 'Source',
    choose: '— Choose —',
    noContactsSourceAvailableInThis: 'No “contacts” source available in this module.',
    suppressedUnsubscribedContactsAreFiltere: 'Suppressed / unsubscribed contacts are filtered out automatically. Every send remains subject to the consent check (CASL / Law 25).',
    cancel: 'Cancel',
    creating: 'Creating…',
    createTheList: 'Create the list',
  },
  emailFormat: {
    nDays: '{{days}} days',
    nHours: '{{hours}} hours',
    immediate: 'immediate',
    n1Day: '1 day',
    n1Hour: '1 hour',
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    scheduled: 'Scheduled',
    sending: 'Sending',
    sent: 'Sent',
    send: 'Send',
    unsubscribed: 'Unsubscribed',
    hardBounce: 'Hard bounce',
    complaint: 'Complaint',
    manual: 'Manual',
    alwaysSend: 'Always send',
    ifNotOpenedPrevStep: 'If not opened (prev. step)',
    ifNotClickedPrevStep: 'If not clicked (prev. step)',
    ifNoReply: 'If no reply',
    call: 'Call',
    manualTask: 'Manual task',
    toHandle: 'To handle',
    providerRequired: 'Provider required',
    failed: 'Failed',
    completed: 'Completed',
    skipped: 'Skipped',
    interested: 'Interested',
    notInterested: 'Not interested',
    outOfOfficeOoo: 'Out of office (OOO)',
    neutral: 'Neutral',
    unclassified: 'Unclassified',
    unread: 'Unread',
    read: 'Read',
    replied: 'Replied',
    pending: 'Pending',
    archived: 'Archived',
    connected: 'Connected',
    error: 'Error',
    disconnected: 'Disconnected',
    good: 'Good',
    toWatch: 'To watch',
    fragile: 'Fragile',
    email: 'Email',
  },
  hooks: {
    noIdentity: 'no identity',
  },
  insights: {
    highBounceRateV: 'High bounce rate ({{v}})',
    cleanTheListAndCheckSpf: 'Clean the list and check SPF/DKIM/DMARC before the next send.',
    lowOpensV: 'Low opens ({{v}})',
    testTheSubjectTheSenderName: 'Test the subject, the sender name and the sending window.',
    highUnsubscribesV: 'High unsubscribes ({{v}})',
    reduceTheFrequencyOrRefineThe: 'Reduce the frequency or refine the targeting of this audience.',
    repliedReplyIesReceived: '{{replied}} reply(ies) received',
    handleThemQuicklyToMaximizeConversion: 'Handle them quickly to maximize conversion.',
    goodClickOpenRateV: 'Good click/open rate ({{v}})',
    theContentResonatesReuseItAs: 'The content resonates — reuse it as a reference.',
    recommendedWindowTuesdayThursday911: 'Recommended window: Tuesday–Thursday, 9–11 a.m.',
    generalB2bRecommendationScheduleAccordin: 'General B2B recommendation — schedule according to your audience\'s time zone.',
    noStepDefined: 'No step defined',
    addAtLeastOneStepBefore: 'Add at least one step before activating the sequence.',
    pausedWithEnrolledEnrolled: 'Paused with {{enrolled}} enrolled',
    scheduledSendsAreSuspendedResumeOr: 'Scheduled sends are suspended — resume or end the sequence.',
    emptystepsStepSWithoutContent: '{{emptySteps}} step(s) without content',
    linkATemplateOrEnterA: 'Link a template or enter a subject + body for each step.',
    highPerformingSequence: 'High-performing sequence',
    totalrepliedReplyIesOutOfTotalsent: '{{totalReplied}} reply(ies) out of {{totalSent}} sends.',
    activeButNoOneEnrolled: 'Active but no one enrolled',
    enrollContactsToStartSending: 'Enroll contacts to start sending.',
    notextTemplateSWithoutAText: '{{noText}} template(s) without a text version',
    aTextAlternativeImprovesInboxPlacement: 'A text alternative improves inbox placement.',
    placeholdernovarsTemplateSWithUndeclared: '{{placeholderNoVars}} template(s) with undeclared variables',
    declareTheVariablesToAvoidAn: 'Declare the {{ … }} variables to avoid an empty render at send time.',
    dupesDuplicateSubjectS: '{{dupes}} duplicate subject(s)',
    distinctSubjectsMakePerformanceTrackingE: 'Distinct subjects make performance tracking easier.',
    noSenderIdentity: 'No sender identity',
    addASenderAddressToBe: 'Add a sender address to be able to send.',
    unverifiedUnverifiedIdentityIes: '{{unverified}} unverified identity(ies)',
    checkTheProviderConfigurationBeforeSendi: 'Check the provider configuration before sending in volume.',
    noconfigIdentityIesWithoutConfiguration: '{{noConfig}} identity(ies) without configuration',
    fillInTheSmtpSendgridSes: 'Fill in the SMTP/SendGrid/SES config to enable real sending.',
    noreplytoIdentityIesWithoutReplyTo: '{{noReplyTo}} identity(ies) without Reply-To',
    aDedicatedReplyToCentralizesAnd: 'A dedicated Reply-To centralizes and secures reply collection.',
    highOverallBouncesV: 'High overall bounces ({{v}})',
    checkTheDomainAuthenticationAndPurge: 'Check the domain authentication and purge invalid addresses.',
    hardbouncesHardBouncesSuppressed: '{{hardBounces}} hard bounces suppressed',
    considerValidatingAddressesBeforeImporti: 'Consider validating addresses before importing.',
    noMailboxEligibleToSend: 'No mailbox eligible to send',
    capsReachedThrottleActiveOrHealth: 'Caps reached, throttle active or health too low — wait or add mailboxes.',
    lowAverageMailboxHealthAvgHealth: 'Low average mailbox health ({{avg_health_score}})',
    slowDownAndLetTheWarmup: 'Slow down and let the warmup raise the score.',
    warmingCountMailboxEsWarmingUp: '{{warming_count}} mailbox(es) warming up',
    capsRiseGraduallyAvoidLargeVolumes: 'Caps rise gradually — avoid large volumes too early.',
    configureASenderIdentity: 'Configure a sender identity',
    aRequiredStepBeforeAnyCampaign: 'A required step before any campaign or sequence.',
    verifyAtLeastOneIdentity: 'Verify at least one identity',
    aVerifiedSenderImprovesDeliverability: 'A verified sender improves deliverability.',
    draftsDraftCampaignS: '{{drafts}} draft campaign(s)',
    finishAndScheduleThemToStart: 'Finish and schedule them to start sending.',
    readyseqSequenceSReadyToActivate: '{{readySeq}} sequence(s) ready to activate',
    activateThemThenEnrollContacts: 'Activate them, then enroll contacts.',
  },
  outreachLayout: {
    emailOutreach: 'Email Outreach',
    campaignsMultichannelSequencesUniboxDeli: 'Campaigns, multichannel sequences, Unibox, deliverability and templates.',
  },
  outreachSettingsPage: {
    senderEmail: 'Sender email',
    senderName: 'Sender name',
    replyTo: 'Reply to',
    signatureHtml: 'Signature (HTML)',
    provider: 'Provider',
    dailyLimit: 'Daily limit',
    minDelayBetweenSendsS: 'Min. delay between sends (s)',
    deliverabilityPosture: 'Deliverability posture',
    healthyConfigurationNoRiskDetected: 'Healthy configuration — no risk detected.',
    sendingCapacityWarmup: 'Sending capacity & warmup',
    liveStateOfTheSendingMailbox: 'Live state of the sending mailbox pool: daily caps (warmup included), volume used, health and eligibility to send now.',
    rotationPool: 'Rotation pool',
    rotationPoolViewUnavailableOnThis: 'Rotation pool view unavailable on this backend.',
    addSenderIdentitiesToBuildA: 'Add sender identities to build a sending pool.',
    capacityUsed: 'Capacity used',
    mailboxes: 'Mailboxes',
    ready: 'Ready',
    warmingUp: 'Warming up',
    avgHealth: 'Avg. health',
    verified: 'Verified',
    remainingDay: 'Remaining / day',
    ready2: 'Ready',
    throttled: 'Throttled',
    unavail: 'Unavail.',
    warmupWarmupStage: 'Warmup #{{warmup_stage}}',
    providerSentTodayEffectiveCapToday: '{{provider}} · {{sent_today}}/{{effective_cap_today}} today',
    identityVerified: 'Identity verified.',
    failedV: 'Failed: {{v}}',
    identityFromEmailResumed: 'Identity {{from_email}} resumed.',
    identityFromEmailPaused: 'Identity {{from_email}} paused.',
    resumingFailed: 'Resuming failed.',
    pausingFailed: 'Pausing failed.',
    newIdentity: '+ New identity',
    senderIdentities: 'Sender identities',
    noIdentity: 'No identity',
    addTheAddressAndProviderSmtp: 'Add the address and provider (SMTP, SendGrid, SES) used for sending.',
    addAnIdentity: 'Add an identity',
    paused: 'Paused',
    sentTodayDailyLimitToday: '· {{sent_today}}/{{daily_limit}} today',
    configPresent: ' · config present',
    noConfig: ' · no config',
    minSecondsBetweenSendsSBetween: ' · {{min_seconds_between_sends}}s between sends',
    replyToReplyTo: '↩ Reply-To: {{reply_to}}',
    signature: '✍ Signature',
    reEnableThisIdentityInThe: 'Re-enable this identity in the sending rotation',
    removeThisIdentityFromTheSending: 'Remove this identity from the sending rotation',
    resume: 'Resume',
    pause: 'Pause',
    checking: 'Checking…',
    verify: 'Verify',
    edit: 'Edit',
    deleteTheIdentity: 'Delete the identity',
    deleteTheIdentityFromEmailIt: 'Delete the identity {{from_email}}? It will no longer be available for campaigns and sequences.',
    invalidJson: 'Invalid JSON.',
    editTheIdentity: 'Edit the identity',
    newIdentity2: 'New identity',
    senderAddress: 'Sender address',
    helloMycompanyCom: 'hello@mycompany.com',
    senderName2: 'Sender name',
    optionalShownInTheRecipientS: 'Optional — shown in the recipient\'s inbox.',
    replyToReplyTo2: 'Reply to (Reply-To)',
    optionalAddressWhereRepliesLandDefault: 'Optional — address where replies land (default: the sender address).',
    repliesMycompanyCom: 'replies@mycompany.com',
    optionalAddedAtTheBottomOf: 'Optional — added at the bottom of every send (the {{ … }} variables are interpreted).',
    pFirstNameAcmeTeamP: '<p>— {{ first_name }}, Acme Team</p>',
    n0NoThrottle: '0 = no throttle',
    providerConfigurationJson: 'Provider configuration (JSON)',
    anEncryptedConfigAlreadyExistsNever: 'An encrypted config already exists (never sent back). Fill in to replace it.',
    eGHostSmtpPort587: 'E.g. { "host": "smtp…", "port": 587, "username": "…", "password": "…" }. Stored encrypted.',
    apiKey: '{ "api_key": "…" }',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    available: 'Available',
    throttled2: 'Throttled',
    exhausted: 'Exhausted',
    warmupDWarmupStage: 'Warmup · d{{warmup_stage}}',
    health: 'Health',
    effectiveCapTodayTodayRemainingToday: '/{{effective_cap_today}} today · {{remaining_today}}',
    remaining: 'remaining',
    delayMinSecondsBetweenSendsS: 'delay {{min_seconds_between_sends}}s',
    stopTheWarmup: 'Stop the warmup',
    startTheWarmup: 'Start the warmup',
    deliverabilityRotationPool: 'Deliverability — rotation pool',
    sendingMailboxRotationRoundRobinDaily: 'Sending mailbox rotation (round-robin), daily caps, progressive warmup and health score — to protect reputation and scale up volume.',
    refresh: 'Refresh',
    noSendingMailbox: 'No sending mailbox',
    addSenderIdentitiesToBuildA2: 'Add sender identities to build a rotation pool.',
    available2: 'Available',
    capacityDay: 'Capacity / day',
    address: 'Address',
    reason: 'Reason',
    source: 'Source',
    added: 'Added',
    remove: 'Remove',
    suppressionList: 'Suppression list',
    theseAddressesWillNeverReceiveAn: 'These addresses will never receive an email (unsubscribes, complaints, hard bounces).',
    addAnAddress: '+ Add an address',
    noSuppressedAddressYet: 'No suppressed address yet.',
    addAnAddress2: 'Add an address',
    emailAddress: 'Email address',
    addressExampleCom: 'address@example.com',
    adding: 'Adding…',
    add: 'Add',
  },
  sendingPage: {
    enrolled: 'Enrolled',
    error: 'Error',
    theWarmupNetworkIsNotAvailable: 'The warmup network is not available on this environment.',
    theOperationFailed: 'The operation failed.',
    warmupNetwork: 'Warmup network',
    sharedReputationPoolYourMailboxesExchang: 'Shared reputation pool: your mailboxes exchange warmup messages with the other members and pull them out of spam, which increases inbox placement.',
    leaveTheNetwork: 'Leave the network',
    joinTheNetwork: 'Join the network',
    theWarmupNetworkIsNotAvailable2: 'The warmup network is not available on this environment yet.',
    participation: 'Participation',
    mailboxes: 'Mailboxes',
    active: 'Active',
    inboxPlacement: 'Inbox placement',
    avgHealth: 'Avg. health',
    labelPlacement: '{{label}} placement',
    spamV: 'Spam: {{v}}%',
    sentTodaySentToday: '· {{sent_today}} sent today',
    receivedTodayReceived: '· {{received_today}} received',
    noActiveMailboxInTheNetwork: 'No active mailbox in the network',
    noEnrolledMailbox: 'No enrolled mailbox',
    joinTheNetworkToEnrollYour: 'Join the network to enroll your sender identities and share reputation.',
    inboxPct: 'Inbox {{pct}}',
    sendingHealth: 'Sending health',
    workspaceDeliverabilityOverviewBounceRat: 'Workspace deliverability overview (bounce rate, replies).',
    deliverabilityReportingIsNotAvailableOn: 'Deliverability reporting is not available on this environment yet.',
    sent: 'Sent',
    openRate: 'Open rate',
    clickRate: 'Click rate',
    replyRate: 'Reply rate',
    bounceRate: 'Bounce rate',
    unsubscribes: 'Unsubscribes',
    newNewDuplicatesDuplicateSCrm: '{{new}} new, {{duplicates}} duplicate(s), {{crm_pushed}} pushed to the CRM.',
    mailboxNotConnectedLinkOrRe: 'Mailbox not connected: link (or re-consent) the OAuth account.',
    syncFailed: 'Sync failed.',
    connectedMailboxes: 'Connected mailboxes',
    gmailOutlookImapMailboxesInTwo: 'Gmail/Outlook/IMAP mailboxes in two-way sync (sending + receiving).',
    connectAMailbox: '+ Connect a mailbox',
    mailboxSyncIsNotAvailableOn: 'Mailbox sync is not available on this environment yet.',
    noConnectedMailbox: 'No connected mailbox',
    connectAMailboxToReceiveReplies: 'Connect a mailbox to receive replies in the Unibox and mirror the exchanges in the CRM.',
    connectAMailbox2: 'Connect a mailbox',
    syncV: 'Sync: {{v}}',
    errorLastError: 'Error: {{last_error}}',
    syncing: 'Syncing…',
    sync: 'Sync',
    theImapConfigurationJsonIsRequired: 'The IMAP configuration (JSON) is required.',
    invalidImapJson: 'Invalid IMAP JSON.',
    anOauthConnectionIdentifierPlatformAuth: 'An OAuth connection identifier (platform-auth) is required.',
    connectionFailed: 'Connection failed.',
    provider: 'Provider',
    emailAddress: 'Email address',
    helloMycompanyCom: 'hello@mycompany.com',
    displayName: 'Display name',
    imapConfigurationJson: 'IMAP configuration (JSON)',
    eGHostImapPort993: 'E.g. { "host": "imap…", "port": 993, "username": "…", "password": "…" }. Stored encrypted.',
    hostImapExampleComPort993: '{ "host": "imap.example.com", "port": 993, "username": "…", "password": "…" }',
    oauthConnectionConnectionId: 'OAuth connection (connection_id)',
    identifierOfAnAlreadyAuthorizedV: 'Identifier of an already authorized {{v}} connection (platform-auth).',
    conn: 'conn_…',
    connecting: 'Connecting…',
    connect: 'Connect',
    cancel: 'Cancel',
    rotationPoolWarmup: 'Rotation pool & warmup',
    healthyVerifiedIdentitiesAreWeightedBy: 'Healthy, verified identities are weighted by their health score when enrolling in a sequence (inbox rotation). Warmup gradually raises the daily sending cap.',
    theSendingPoolIsNotAvailable: 'The sending pool is not available yet.',
    noSenderIdentity: 'No sender identity',
    addIdentitiesInSettingsToBuild: 'Add identities in Settings to build a rotation pool.',
    verified: '✓ Verified',
    notVerified: '⚠ Not verified',
    warmupWarmupInitialWarmupTargetD: 'Warmup {{warmup_initial}} → {{warmup_target}}/d',
    warmupOff: 'Warmup off',
    warmup: 'Warmup',
    mailboxHealth: 'Mailbox health',
    todaySCap: 'Today\'s cap',
    capSent: '/ {{cap}} sent',
    remainingTodayRemaining: '· {{remaining_today}} remaining',
    configuredLimitDailyLimitDay: 'Configured limit: {{daily_limit}}/day',
    savingFailed: 'Saving failed.',
    warmupFromEmail: 'Warmup — {{from_email}}',
    enableProgressiveWarmup: 'Enable progressive warmup',
    initialVolumeD: 'Initial volume/d',
    incrementD: 'Increment/d',
    targetD: 'Target/d',
    theCapRisesFromInitialIn: 'The cap rises from {{initial}} in steps of {{increment}}/day up to {{target}}, bounded by the daily limit ({{daily_limit}}) and reduced if health drops.',
    saving: 'Saving…',
    save: 'Save',
    domainsDns: 'Domains & DNS',
    checkYourSenderDomainsSpfDkim: 'Check your sender domains\' SPF / DKIM / DMARC records live, with the exact record to publish when one is missing.',
    noDomain: 'No domain',
    addASenderIdentityToCheck: 'Add a sender identity to check its DNS records.',
    theDeliverabilityPolicyIsNotAvailable: 'The deliverability policy is not available on this environment yet.',
    deliverabilityPolicy: 'Deliverability policy',
    requirementsAppliedToEveryMarketingSend: 'Requirements applied to every marketing send (CASL / CAN-SPAM), in addition to the one-click unsubscribe added automatically.',
    physicalPostalAddress: 'Physical postal address',
    mandatoryInEveryCommercialMessageA: 'Mandatory in every commercial message. A marketing send without an address is refused.',
    acmeInc123MainStreetMontreal: 'Acme Inc., 123 Main Street, Montreal, QC, Canada',
    blockSendsFromAnUnauthenticatedDomain: 'Block sends from an unauthenticated domain (SPF/DKIM)',
    failClosedIfTheSenderDomain: 'Fail-closed: if the sender domain has neither SPF nor DKIM published, the marketing send is refused and the missing record is shown below.',
    customTrackingDomain: 'Custom tracking domain',
    showTheOpenClickAndUnsubscribe: 'Show the open, click and unsubscribe links on your own domain (e.g.',
    trackYourdomainCom: 'track.yourdomain.com',
    insteadOfThePlatformSDefault: ') instead of the platform\'s default domain.',
    trackingDomain: 'Tracking domain',
    withoutHttpSEGTrack: 'Without http(s):// — e.g. track.acme.com',
    trackAcmeCom: 'track.acme.com',
    schema: 'Schema',
    https: 'https',
    http: 'http',
    dkimSelector: 'DKIM selector',
    optionalReplacesThePlatformSDefault: 'Optional — replaces the platform\'s default selector for this workspace.',
    eGS1: 'e.g. s1',
    hotProspectAlert: 'Hot prospect alert',
    inAdditionToTheAutomaticUpdate: 'In addition to the automatic update of the contact\'s score in the CRM, get a notification (generic webhook or Slack Incoming Webhook) as soon as a prospect actually replies (excluding out-of-office auto-replies) or clicks heavily on a send.',
    webhookUrl: 'Webhook URL',
    eGASlackIncomingWebhook: 'E.g. a Slack Incoming Webhook URL, or any endpoint accepting JSON via POST. Leave empty to disable.',
    httpsHooksSlackComServices: 'https://hooks.slack.com/services/…',
    mailboxSync: 'Mailbox sync',
    howOftenIncomingEmailsAreFetched: 'How often incoming emails are fetched automatically into the Unibox and the CRM for connected mailboxes.',
    mailboxSyncFrequencyMinutes: 'Mailbox sync frequency (minutes)',
    automaticSyncIntervalForIncomingEmails: 'Automatic sync interval for incoming emails. Leave empty to use the default (5 minutes). Minimum 1 minute.',
    saveThePolicy: 'Save the policy',
    saved: 'Saved.',
    cnameCheckFor: 'CNAME check for',
    verified2: '✓ Verified',
    notVerified2: '✕ Not verified',
    checking: 'Checking…',
    verify: 'Verify',
    liveDnsCheckIsNotAvailable: 'Live DNS check is not available (dnspython required on the server).',
    type: 'Type',
    host: 'Host',
    value: 'Value',
    theCnameCorrectlyPointsToExpected: 'The CNAME correctly points to {{expected_target}}. Tracking links use this domain.',
    required: 'Required',
    recommended: 'Recommended',
    authenticated: 'Authenticated',
    authenticationIncomplete: 'Authentication incomplete',
    dkimSelector2: 'DKIM selector',
    dkimSelectorForDomain: 'DKIM selector for {{domain}}',
    checkDns: 'Check DNS',
    recordsToPublishAtYourRegistrar: 'Records to publish at your registrar:',
    spfDkimAndDmarcAreCorrectly: 'SPF, DKIM and DMARC are correctly published — nothing to add.',
    notRegistered: 'Not registered',
  },
  sequencesPage: {
    ifNotOpened: 'if not opened',
    ifNotClicked: 'if not clicked',
    ifNoReply: 'if no reply',
    sequenceName: 'Sequence name',
    newSequence: '+ New sequence',
    sequences: 'Sequences',
    noSequences: 'No sequences',
    aSequenceChainsSeveralEmailsSpaced: 'A sequence chains several emails spaced out over time.',
    createASequence: 'Create a sequence',
    lengthStepS: '{{length}} step(s)',
    noStepDefined: 'No step defined',
    lengthAutomatedMultichannelStepS: '{{length}} automated multichannel step(s)',
    newSequence2: 'New sequence',
    prospectOnboarding: 'Prospect onboarding',
    youWillAddTheStepsRight: 'You will add the steps right after creating it.',
    create: 'Create',
    cancel: 'Cancel',
    enroll: 'Enroll',
    pause: 'Pause',
    addAtLeastOneStep: 'Add at least one step',
    enable: 'Enable',
    deleteTheSequence: 'Delete the sequence',
    sequenceFlow: 'Sequence flow',
    steps: 'Steps',
    step: '+ Step',
    noStepsAddOneToGet: 'No steps. Add one to get started.',
    delayV: 'Delay: {{v}}',
    moveStepUp: 'Move step up',
    moveStepDown: 'Move step down',
    deleteTheStep: 'Delete the step',
    channel: 'Channel',
    delayHours: 'Delay (hours)',
    sendCondition: 'Send condition',
    template: 'Template',
    customContent: '— Custom content —',
    topic: 'Topic',
    helloPrenom: 'Hello {{ prenom }}',
    htmlBody: 'HTML body',
    taskTitle: 'Task title',
    labelEGConnectionNote: '{{label}} — e.g. Connection + note',
    instructionsMessage: 'Instructions / message',
    prenomVariablesAvailableThisStepCreates: '{{ prenom }} variables available. This step creates a task to handle (multichannel queue).',
    messageOrScriptToSendOn: 'Message or script to send on this channel…',
    saving: 'Saving…',
    saveTheSteps: 'Save the steps',
    enrollContacts: 'Enroll contacts',
    deleteTheSequenceNameOngoingEnrollments: 'Delete the sequence “{{name}}”? Ongoing enrollments will be stopped. This cannot be undone.',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    sequenceSettings: 'Sequence settings',
    sendingWindowDailyCapAndStop: 'Sending window, daily cap and stop conditions — applied by the worker to each scheduled send of this sequence.',
    sendingDays: 'Sending days',
    noDaySelectedEveryDay: 'No day selected = every day.',
    sendsCapDay: 'Sends cap / day',
    emptyNoSpecificCap: 'Empty = no specific cap.',
    eG200: 'e.g. 200',
    startHour: 'Start hour',
    n023UtcHour: '0–23, UTC hour.',
    eG8: 'e.g. 8',
    endHour: 'End hour',
    n023UtcHourExclusive: '0–23, UTC hour (exclusive).',
    eG18: 'e.g. 18',
    stopEnrollmentAsSoonAsA: 'Stop enrollment as soon as a contact replies',
    stopEnrollmentAsSoonAsA2: 'Stop enrollment as soon as a contact clicks',
    tracking: 'Tracking',
    trackOpensOpenPixel: 'Track opens (open pixel)',
    trackClicksLinkRewriting: 'Track clicks (link rewriting)',
    turnOffForADeliverabilitySensitive: 'Turn off for a deliverability-sensitive send. The unsubscribe link and the List-Unsubscribe header are always present (CASL/CAN-SPAM).',
    saveTheSettings: 'Save the settings',
    saved: 'Saved.',
    stepV: 'Step {{v}}',
    performance: 'Performance',
    sent: 'Sent',
    opens: 'Opens',
    clicks: 'Clicks',
    responses: 'Responses',
    funnelByStep: 'Funnel by step',
    trend30Days: 'Trend — 30 days',
    timeSeriesUnavailableOnThisBackend: 'Time series unavailable on this backend.',
    noSendsInTheLast30: 'No sends in the last 30 days.',
    sends: 'Sends',
    dailySends: 'Daily sends',
    dailyOpens: 'Daily opens',
    recommendations: 'Recommendations',
    healthySequence: 'Healthy sequence.',
    sequence: 'Sequence',
    status: 'Status',
    repl: 'Repl.',
    sequencePerformance: 'Sequence performance',
    performanceReportUnavailableOnThisBacken: 'Performance report unavailable on this backend.',
    noPerformanceDataYet: 'No performance data yet.',
    loadingTheFunnel: 'Loading the funnel…',
    noData: 'No data',
    enrollContactsAndLetTheSequence: 'Enroll contacts and let the sequence run to see the funnel.',
    enrolled: 'Enrolled',
    stepPosition: 'Step {{position}}',
    lengthSkippedV: ' — {{length}} skipped ({{v}})',
    lengthContactSEnrolledSkipped: '{{length}} contact(s) enrolled{{skipped}}.',
    enrollmentFailed: 'Enrollment failed.',
    emailAddresses: 'Email addresses',
    onePerLineOrCommaSeparated: 'One per line (or comma-separated).',
    prospect1ExampleComProspect2ExampleCom: 'prospect1@example.com\nprospect2@example.com',
    senderIdentitiesRotationPool: 'Sender identities (rotation pool)',
    selectSeveralToSpreadTheSends: 'Select several to spread the sends across mailboxes, weighted by each one\'s health.',
    noSenderIdentityConfigured: 'No sender identity configured.',
    identitiesSelectedTheSendsWillBe: 'identities selected — the sends will be spread among them.',
    enrolling: 'Enrolling…',
    close: 'Close',
    couldNotLoadTheAB: 'Could not load the A/B tests.',
    stepABTests: 'Step A/B tests',
    subjectContentComparedPerStep: 'Subject / content compared per step',
    createSeveralVariantsOfAStep: 'Create several variants of a step; sending splits them by weight, then you designate the winner (best reply rate), which is then always sent.',
    aBTestsAreNotAvailable: 'A/B tests are not available on this environment for now.',
    loadingTheABTests: 'Loading the A/B tests…',
    noSavedStep: 'No saved step',
    addAndSaveAtLeastOne: 'Add and save at least one step to set up an A/B test.',
    variantS: 'variant(s)',
    noTest: 'No test',
    couldNotLoadTheVariants: 'Could not load the variants.',
    couldNotDeleteTheVariant: 'Could not delete the variant.',
    winnerDesignatedVariantWinnerLabelIt: 'Winner designated: variant “{{winner_label}}”. It will always be sent from now on.',
    noVariantHasReachedMinsentSend: 'No variant has reached {{minSent}} send(s) yet — test inconclusive.',
    couldNotDesignateTheWinner: 'Could not designate the winner.',
    loadingVariants: 'Loading variants…',
    noVariantsAddAtLeastTwo: 'No variants. Add at least two to start an A/B test.',
    lengthVariantSSplitByWeight: '{{length}} variant(s) — split by weight at send time.',
    variant: '+ Variant',
    noABTestYet: 'No A/B test yet',
    createSeveralVariantsOfThisStep: 'Create several variants of this step (different subject or content) to compare their performance.',
    createAVariant: 'Create a variant',
    variant2: 'Variant',
    weight: 'Weight',
    opens2: 'Opens',
    replyRate: 'Reply rate',
    actions: 'Actions',
    customContent2: 'Custom content',
    winner: '🏆 Winner',
    leading: 'Leading',
    disabled: 'Disabled',
    deleteTheVariantLabel: 'Delete the variant {{label}}',
    minSendsBeforeDeciding: 'Min. sends before deciding',
    analyzing: 'Analyzing…',
    designateTheWinner: '🏆 Designate the winner',
    designatesTheVariantWithTheBest: 'Designates the variant with the best reply rate (ties broken on opens) that reached the threshold. It then becomes the only variant sent.',
    deleteTheVariant: 'Delete the variant',
    deleteTheVariantLabelItsSending: 'Delete the variant “{{label}}”? Its sending statistics will be lost.',
    couldNotCreateTheVariant: 'Could not create the variant.',
    newVariant: 'New variant',
    creating: 'Creating…',
    createTheVariant: 'Create the variant',
    label: 'Label',
    eGABShortSubject: 'E.g. A, B, “Short subject”…',
    relativeSendShare0VariantDisabled: 'Relative send share (0 = variant disabled).',
    templateOptional: 'Template (optional)',
    subject: 'Subject',
    title: 'Title',
    varyTheAngleTheHookOr: 'Vary the angle, the hook or the call to action…',
    atSendTimeAVariantIs: 'At send time, a variant is drawn at random according to its weight. Once enough data is collected, designate the winner from the results table.',
    loadingSequences: 'Loading sequences…',
    couldNotRefreshSequences: 'Could not refresh the sequences',
    couldNotLoadSequences: 'Could not load the sequences',
    retry: 'Retry',
  },
  tasksPage: {
    unknownError: 'Unknown error',
    multichannelTasks: 'Multichannel tasks',
    theQueueOfYourSequencesNon: 'The queue of your sequences\' non-email steps (LinkedIn, SMS, WhatsApp, call, manual task) — to handle, complete or skip.',
    loading: 'Loading…',
    refresh: '↻ Refresh',
    channel: 'Channel',
    allChannels: 'All channels',
    status: 'Status',
    allStatuses: 'All statuses',
    sequence: 'Sequence',
    allSequences: 'All sequences',
    owner: 'Owner',
    filterByOwner: 'Filter by owner…',
    resetTheFilters: 'Reset the filters',
    task: 'task',
    loadingTheQueue: 'Loading the queue…',
    theMultichannelTaskQueueIsNot: 'The multichannel task queue is not available on this environment yet. It will appear as soon as the email service exposes the tasks.',
    noTasksInTheQueue: 'No tasks in the queue',
    noTaskMatchesTheseFiltersBroaden: 'No task matches these filters. Broaden the search.',
    yourSequencesNonEmailStepsLinkedin: 'Your sequences\' non-email steps (LinkedIn, SMS, call…) will appear here as soon as a contact reaches one of these steps.',
    couldNotCompleteTheTask: 'Could not complete the task.',
    couldNotSkipTheTask: 'Could not skip the task.',
    automaticSendTriggered: 'Automatic send triggered.',
    automaticSendUnavailableForThisChannel: 'Automatic send unavailable for this channel.',
    sendingFailed: 'Sending failed.',
    openTheTaskTitle: 'Open the task {{title}}',
    untitledTask: 'Untitled task',
    unknownContact: 'Unknown contact',
    dueV: '🗓 Due: {{v}}',
    send: '📨 Send',
    complete: '✓ Complete',
    dismiss: 'Dismiss',
    details: 'Details',
    actionNotPossible: 'Action not possible.',
    save: 'Save',
    taskDetails: 'Task details',
    createdV: '🕘 Created: {{v}}',
    closedV: '✔ Closed: {{v}}',
    instructionsMessage: 'Instructions / message',
    ownerNameOrEmail: 'Owner name or email',
    timeline: 'Timeline',
    outcomeNote: 'Outcome note',
    whatWasDoneTheAnswerReceived: 'What was done / the answer received.',
    eGConnectionAcceptedMessageSent: 'E.g. Connection accepted, message sent…',
    result: 'Result',
    sendAutomatically: '📨 Send automatically',
    skipTheTask: 'Skip the task',
  },
  templateBlockEditor: {
    title: 'Title',
    paragraph: 'Paragraph',
    buttonCta: 'Button (CTA)',
    bulletedList: 'Bulleted list',
    calloutInfoCard: 'Callout / info card',
    divider: 'Divider',
    quote: 'Quote',
    signature: 'Signature',
    aPunchyTitle: 'A punchy title',
    helloPrenomBrYourParagraphHere: 'Hello {{ prenom }},<br />Your paragraph here.',
    learnMore: 'Learn more',
    firstPoint: 'First point',
    secondPoint: 'Second point',
    keyTakeaway: 'Key takeaway',
    yourCalloutContent: 'Your callout content.',
    aQuoteOrATestimonial: 'A quote or a testimonial.',
    trTdStylePadding24px8px: '<tr><td style="padding:24px 8px 8px 8px;font-family:{{FONT}};font-size:16px;line-height:26px;color:{{TEXT}};">{{closing}}<br /><strong>{{ coalesce(sender_name, sender_signature, "The team") }}</strong></td></tr>',
    moveTheBlockUp: 'Move the block up',
    moveTheBlockDown: 'Move the block down',
    deleteTheBlock: 'Delete the block',
    pointV: 'Point {{v}}',
    removeThisPoint: 'Remove this point',
    addAPoint: '+ Add a point',
    titleText: 'Title text',
    helloPrenom: 'Hello {{ prenom }}',
    contentSimpleHtmlAllowed: 'Content (simple HTML allowed)',
    variablesPrenomEntreprise: 'Variables: {{ prenom }}, {{ entreprise }}…',
    mutedTextSubtitleSecondaryMention: 'Muted text (subtitle / secondary mention)',
    label: 'Label',
    linkUrl: 'Link (URL)',
    https: 'https://…',
    calloutTitle: 'Callout title',
    content: 'Content',
    dividerLineNoSettings: 'Divider line — no settings.',
    closing: 'Closing',
    theSenderSNameIsAdded: 'The sender\'s name is added automatically when sending.',
    bestRegards: 'Best regards,',
    noBlocksAddOneBelowTo: 'No blocks — add one below to compose the email body.',
    addABlock: 'Add a block:',
  },
  templatesPage: {
    name: 'Name',
    category: 'Category',
    topic: 'Topic',
    htmlBody: 'HTML body',
    textBody: 'Text body',
    installsTheRichTemplatesHeaderLogo: 'Installs the rich templates (header, logo, CTA, unsubscribe) in French and English.',
    installing: 'Installing…',
    richTemplates: '✨ Rich templates',
    newTemplate: '+ New template',
    templates: 'Templates',
    noTemplate: 'No template',
    installTheRichTemplatesFrenchEnglish: 'Install the rich templates (French + English) or create a reusable template (subject + HTML body with {{ prenom }} variables).',
    createATemplate: 'Create a template',
    lengthVar: '{{length}} var. · ',
    deleteTheTemplate: 'Delete the template',
    noSubject: '(no subject)',
    deleteTheTemplateNameItWill: 'Delete the template “{{name}}”? It will be removed from the campaigns and steps that use it.',
    categories: 'Categories',
    textVersion: 'Text version',
    variablesTemplate: 'Variables / template',
    templateQuality: 'Template quality',
    yourTemplatesAreWellStructured: 'Your templates are well structured.',
    highRisk: 'High risk',
    mediumRisk: 'Medium risk',
    lowRisk: 'Low risk',
    spamCheck: 'Spam check',
    analyzing: 'Analyzing…',
    analyze: 'Analyze',
    spamAnalysisUnavailableOnThisBackend: 'Spam analysis unavailable on this backend.',
    scoreScore100: '· score {{score}}/100',
    linkCountLinkS: '{{link_count}} link(s)',
    noRiskyWordOrSignalDetected: 'No risky word or signal detected.',
    theRawHtmlWasEditedManually: 'The raw HTML was edited manually — switching back to block mode starts from a blank template (no automatic conversion).',
    thisRawHtmlWasNotCreated: 'This raw HTML was not created with the block editor — a new block content was initialized.',
    editTheTemplate: 'Edit the template',
    newTemplate2: 'New template',
    addAtLeastOneBlockWith: 'Add at least one block with content.',
    theHtmlBodyCannotBeEmpty: 'The HTML body cannot be empty.',
    followUpD3: 'Follow-up D+3',
    optionalUsedForFiltering: 'Optional — used for filtering.',
    prospecting: 'prospecting',
    helloPrenom: 'Hello {{ prenom }}',
    emailBody: 'Email body',
    blocks: '🧱 Blocks',
    rawHtmlAdvanced: '</> Raw HTML (advanced)',
    pHelloPrenomP: '<p>Hello {{ prenom }},</p>',
    optionalFallbackVersionWithoutHtml: 'Optional — fallback version without HTML.',
    variables: 'Variables',
    commaSeparatedEGPrenomEntreprise: 'Comma-separated — e.g. prenom, entreprise.',
    prenomEntreprise: 'prenom, entreprise',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    preview: 'Preview',
    localPreviewVariablesNotMergedSave: 'Local preview (variables not merged) — save the template to enable the server-rendered preview with your test values.',
    localPreview: 'Local preview',
    testValuesMergeVars: 'Test values (merge_vars)',
    rendering: 'Rendering…',
    generateThePreview: 'Generate the preview',
    subject: 'Subject:',
    htmlPreview: 'HTML preview',
    installTheEnrichedTemplates: '✨ Install the enriched templates',
  },
  uniboxPage: {
    error: 'Error',
    unibox: 'Unibox',
    allTheRepliesFromYourSending: 'All the replies from your sending mailboxes, gathered and labeled by AI.',
    refresh: 'Refresh',
    totalReplyIes: '{{total}} reply(ies)',
    status: 'Status',
    all: 'All',
    aiLabel: 'AI label',
    all2: 'All',
    campaign: 'Campaign',
    sequence: 'Sequence',
    assigned: 'Assigned',
    theUniboxIsNotAvailableOn: 'The Unibox is not available on this environment yet (service being deployed or no mailbox connected). Try again later.',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    sizeSelected: ' · {{size}} selected',
    noReplies: 'No replies',
    repliesToYourCampaignsAndSequences: 'Replies to your campaigns and sequences will appear here, labeled automatically.',
    selectAReply: 'Select a reply',
    chooseAReplyOnTheLeft: 'Choose a reply on the left to read it, classify it and answer it.',
    selectTheReplyFromFromEmail: 'Select the reply from {{from_email}}',
    noSubject: '(no subject)',
    actionNotPossible: 'Action not possible.',
    receivedV: 'Received: {{v}}',
    assignedAssignee: '· Assigned: {{assignee}}',
    reply: 'Reply',
    analyzing: 'Analyzing…',
    reclassifyAi: 'Reclassify (AI)',
    archive: 'Archive',
    markReplied: 'Mark replied',
    assignTo: 'Assign to',
    eGRep1FirstName: 'e.g. rep-1 / first name',
    assign: 'Assign',
    remove: 'Remove',
    addALabel: 'Add a label',
    toCallBack: 'to-call-back',
    add: 'Add',
    bulkActionFailed: 'Bulk action failed.',
    replyIesSelected: 'reply(ies) selected',
    clearTheSelection: 'Clear the selection',
    applyTheStatus: 'Apply the status',
    unassign: 'Unassign',
    batchA: 'batch-a',
    addTheLabel: 'Add the label',
    draftGeneratedByAiProofreadAnd: 'Draft generated by AI — proofread and edit before sending.',
    aiWritingUnavailableNoLlmGateway: 'AI writing unavailable (no LLM gateway configured). Write the reply manually.',
    couldNotGenerateADraft: 'Could not generate a draft.',
    addingTheAttachmentFailed: 'Adding the attachment failed.',
    couldNotMarkAsReplied: 'Could not mark as replied.',
    couldNotSend: 'Could not send.',
    replyToFromEmail: 'Reply to {{from_email}}',
    topic: 'Topic',
    message: 'Message',
    writing: 'Writing…',
    writeWithAi: '✨ Write with AI',
    helloThankYouForYourFeedback: 'Hello, thank you for your feedback…',
    attachments: 'Attachments',
    deckPdfDocument10MbMax: 'Deck, PDF, document — 10 MB max per file.',
    vKb: '({{v}} KB)',
    removeFilename: 'Remove {{filename}}',
    sending: 'Sending…',
    attachAFile: '+ Attach a file',
    sendTheReply: 'Send the reply',
    markAsReplied: 'Mark as replied',
    close: 'Close',
  },
  widgets: {
    rate: 'Rate',
    noDataYet: 'no data yet',
    trend: 'Trend',
    allGreen: 'All green.',
    noStepToDisplay: 'No step to display.',
    startDelaylabel: 'Start · {{delayLabel}}',
    sent: 'sent',
    openrateOpens: '{{openRate}}% opens',
    indicatorUnavailableRightNow: 'Indicator unavailable right now.',
    noData: 'No data.',
  },
  workspaceKpiBand: {
    deliverabilityOverview: 'Deliverability overview',
    overview: 'Overview',
    messageSVSent: 'message(s) · {{v}} sent',
    deliverability: 'Deliverability',
    opens: 'Opens',
    clicks: 'Clicks',
    responses: 'Responses',
    sendsLast14Days: 'Sends — last 14 days',
    inTotal: 'in total',
    dailySendsOver14Days: 'Daily sends over 14 days',
    capacityDay: 'Capacity / day',
    remaining: 'Remaining',
    readyMailboxes: 'Ready mailboxes',
    avgHealth: 'Avg. health',
    ofTodaySCapacityUsed: '% of today\'s capacity used',
    warmingCountWarmingUp: ' · {{warming_count}} warming up',
  },
};

export default en;
